import { useMemo } from 'react';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { getCurrencyDecimalPlaces, minorToMajorUnits } from '@shared/utils/currency/minorUnits';
import type { FundSplitArgs, FundSplitDisplay } from './useFundSplitDisplay.types';

/**
 * The travel credit / cash fund split of the couple's balance, formatted in the fund currency.
 * Exact, never floored, so the parts always add up to the total. Undefined without travel credit or with an unknown figure.
 */
export const useFundSplitDisplay = ({ travelCreditMinorUnits, cashFundMinorUnits, currency }: FundSplitArgs): FundSplitDisplay | undefined => {
  const { formatCurrency } = useCurrencyFormatter();

  return useMemo(() => {
    if (!currency || typeof travelCreditMinorUnits !== 'number' || typeof cashFundMinorUnits !== 'number' || travelCreditMinorUnits <= 0 || cashFundMinorUnits < 0) {
      return undefined;
    }
    const travelCredit = minorToMajorUnits(travelCreditMinorUnits, currency);
    const cashFund = minorToMajorUnits(cashFundMinorUnits, currency);
    const total = minorToMajorUnits(travelCreditMinorUnits + cashFundMinorUnits, currency);
    // One precision for all three: "$825.50 + $100" would read as if the cash fund had been rounded.
    const precision = Number.isInteger(travelCredit) && Number.isInteger(cashFund) ? 0 : getCurrencyDecimalPlaces(currency);
    const format = (majorUnits: number) =>
      formatCurrency({ priceFloatingPointDecimalString: majorUnits.toString(), priceCurrencyCode: currency, formatForm: 'auto', formatPrecision: precision });
    return { travelCreditDisplay: format(travelCredit), cashFundDisplay: format(cashFund), totalDisplay: format(total) };
  }, [travelCreditMinorUnits, cashFundMinorUnits, currency, formatCurrency]);
};

/** True only when the balance is known to be all cash fund: zero travel credit and a readable cash fund. Unknown figures prove nothing. */
export const isBalanceAllCashFund = ({ travelCreditMinorUnits, cashFundMinorUnits }: FundSplitArgs): boolean =>
  travelCreditMinorUnits === 0 && typeof cashFundMinorUnits === 'number';
